import {
  Dispatch,
  FC,
  HTMLProps,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import CancelModal from '$components/CancelModal/CancelModal';
import FormFooter from '$components/FormFooter/FormFooter';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import { usePositions } from '$store/modules/companies/hooks/use-positions';
import { SaveState } from '$types/ForrmType';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { useDuplicatePositionWithCustomRosterMutation } from '../../store/modules/companies';

interface DuplicateCustomRosterProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const DuplicateRosterByPasteCandidatesIds: FC<DuplicateCustomRosterProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const [t] = useTranslation('global');
  const candidateIdsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const handleClose = () => {
    setIsOpen(false);
  };

  const { activePosition } = useAppSelector((state) => state.company);

  const [saveState, setSaveState] = useState(SaveState.IDLE);
  const [showCancel, setShowCancel] = useState(false);
  const [
    duplicatePositionWithCustomRoster,
    { isLoading, status, data: duplicatedPosition, isSuccess },
  ] = useDuplicatePositionWithCustomRosterMutation();

  const { addPositionAction } = usePositions();

  useEffect(() => {
    if (status === QueryStatus.fulfilled && duplicatedPosition && isSuccess) {
      addPositionAction(duplicatedPosition);
      setIsOpen(false);
    }
  }, [addPositionAction, duplicatedPosition, isSuccess, setIsOpen, status]);

  const handleCancel = () => {
    setShowCancel(true);
  };
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement> & HTMLProps<HTMLFormElement>
  ) => {
    e.preventDefault();
    setSaveState(SaveState.PENDING);

    if (candidateIdsTextareaRef.current) {
      const candidatesIds = candidateIdsTextareaRef.current.value
        .split(/,|\n/g)
        .reduce((prev, act) => {
          if (!Number.isNaN(act) && !Number.isNaN(parseInt(act, 10)))
            prev.push(parseInt(act, 10));
          return prev;
        }, [] as number[]);
      await duplicatePositionWithCustomRoster({
        position: activePosition!,
        candidateId: candidatesIds || [],
      });
      setSaveState(SaveState.IDLE);
      setIsOpen(false);
    }
  };

  const handleCancelModal = (isCancel: boolean) => {
    if (isCancel) {
      setIsOpen(false);
    }

    setShowCancel(false);
  };

  return (
    <ModalInTailwind isOpen={isOpen} closeModal={handleClose}>
      <div className="bg-white p-4 rounded ">
        <header data-testid="custom-roster-modal">
          <h3 className="text-brand-darkgrey">
            {t('DuplicateJob.PasteCandidatesIds')}
          </h3>
        </header>
        <form onSubmit={handleSubmit}>
          <textarea
            ref={candidateIdsTextareaRef}
            className="p-2 border border-gray-200 rounded w-full min-h-52"
          />
          <div style={{ marginTop: '1rem' }}>
            <FormFooter
              translate={t}
              saveState={saveState}
              submit
              onClickCancel={handleCancel}
              disabled={isLoading}
              useMobile={false}
            />
          </div>
          {showCancel && handleCancelModal && (
            <CancelModal onClickCancelModal={handleCancelModal} />
          )}
        </form>
      </div>
    </ModalInTailwind>
  );
};

export default DuplicateRosterByPasteCandidatesIds;
